import { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import { PageHeader, Panel, EmptyState } from "../../components/Bits";
import { Modal } from "./Students";
import { api, formatApiError } from "../../lib/api";
import { Plus, Trash2, Megaphone } from "lucide-react";
import { toast } from "sonner";

const PRIORITY_TINT = {
  low: "bg-[#EAE5DE] text-[#5A5652]",
  normal: "bg-[#DCE3D6] text-[#4A5D3E]",
  high: "bg-[#FBEED7] text-[#8C6D31]",
  urgent: "bg-[#F5D8D8] text-[#8C3F3F]",
};

export default function AdminNotices() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", priority: "normal" });
  const [saving, setSaving] = useState(false);

  async function load() { const { data } = await api.get("/notices"); setRows(data); }
  useEffect(() => { load(); }, []);

  async function save(e) {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/admin/notices", form);
      toast.success("Notice posted");
      setOpen(false); setForm({ title: "", content: "", priority: "normal" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  async function remove(n) {
    if (!window.confirm("Delete this notice?")) return;
    await api.delete(`/admin/notices/${n.id}`);
    toast.success("Deleted"); load();
  }

  return (
    <AppShell>
      <PageHeader
        overline="Communication"
        title="Notice board"
        subtitle="Share announcements with everyone in the house."
        actions={
          <button onClick={() => setOpen(true)} data-testid="add-notice-btn" className="bg-terracotta text-white px-5 py-2.5 rounded-md text-sm inline-flex items-center gap-2">
            <Plus className="h-4 w-4" /> New notice
          </button>
        }
      />

      {rows === null ? (
        <div className="text-sm text-warm">Loading…</div>
      ) : rows.length === 0 ? (
        <Panel><EmptyState title="No notices yet" hint="Post your first announcement to all residents." /></Panel>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="notices-grid">
          {rows.map((n) => (
            <article key={n.id} className="bg-white border border-warm rounded-lg p-5 card-lift" data-testid={`notice-${n.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Megaphone className="h-4 w-4 text-[#A67B5B]" />
                  <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${PRIORITY_TINT[n.priority] || PRIORITY_TINT.normal}`}>{n.priority}</span>
                </div>
                <button onClick={() => remove(n)} className="p-1.5 hover:bg-[#F5D8D8] rounded-md"><Trash2 className="h-4 w-4 text-[#8C3F3F]" /></button>
              </div>
              <h3 className="font-heading text-2xl text-deep mt-3">{n.title}</h3>
              <p className="text-warm text-sm mt-2 whitespace-pre-wrap leading-relaxed">{n.content}</p>
              <div className="mt-4 text-xs text-warm">By {n.created_by} · {new Date(n.created_at).toLocaleString()}</div>
            </article>
          ))}
        </div>
      )}

      {open && (
        <Modal title="Post a notice" onClose={() => setOpen(false)}>
          <form onSubmit={save} className="space-y-4">
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-deep font-medium mb-1.5 block">Title *</span>
              <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={input} data-testid="notice-title" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-deep font-medium mb-1.5 block">Content *</span>
              <textarea required rows={6} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className={input} data-testid="notice-content" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-deep font-medium mb-1.5 block">Priority</span>
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className={input}>
                <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option>
              </select>
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="px-5 py-2 rounded-md text-sm border border-warm">Cancel</button>
              <button disabled={saving} type="submit" data-testid="save-notice-btn" className="bg-terracotta text-white px-6 py-2 rounded-md text-sm disabled:opacity-60">{saving ? "Posting…" : "Post notice"}</button>
            </div>
          </form>
        </Modal>
      )}
    </AppShell>
  );
}

const input = "w-full px-3 py-2 rounded-md border border-warm bg-white focus:outline-none focus:ring-2 focus:ring-[#E8C2B8] focus:border-[#C86F58] text-sm";
